import {
  Result,
  ResultSortCriteria,
  ResultSortCriteriaIdEnum,
  ResultSortScoreCriteria,
  ScoreCriteriaDetail,
  ScoreCriteriaDetailTypeEnum as SearchScoreCriteriaDetailTypeEnum
} from '@empathybroker/tsclient-search';

import { RootScoreCriteriaDetailTypeEnum, ScoreCriteriaDetailTypeEnum } from '../enums';

import { Dictionary, ExplainScoreCriteriaMessages, ExplainScoreMessages } from '../models';

/**
 * Summary. Explain utils class. The aim of this utils is to give support for the interface giving useful methods to print debug data
 */
export class ExplainUtils {

  /**
   * Summary. Result with grouped score
   *
   * Description. Some score details are composed of more subdetails. This method will return all the
   * details grouped by rootCriteria.
   *
   * @param {Result} result
   *
   * @return {Result} result with grouped score criterias / undefined if result hasn't got any debug data.
   */
  public static getResultWithGroupedScore(result: Result): Result {
    return {
      ...result,
      debug: {
        ...result.debug,
        sort: result.debug.sort.map((resultSortCriteria: ResultSortCriteria) => {
          if (resultSortCriteria.id === ResultSortCriteriaIdEnum.SCORE) {
            return ExplainUtils.getResultGroupedScoreCriteria(resultSortCriteria as ResultSortScoreCriteria);
          }
          return resultSortCriteria;
        })
      }
    };
  }

  /**
   * Summary. get Result score criteria
   *
   * Description. Get the criteria data when the sortCriteria Id is SCORE
   *
   * @param {result} result
   *
   * @return {ResultSortScoreCriteria}
   */
  public static getResultScoreCriteria(result: Result): ResultSortScoreCriteria {
    const resultScoreCriteria = (result.debug.sort.find((sortCriteria: ResultSortCriteria) =>
      (sortCriteria.id === ResultSortCriteriaIdEnum.SCORE)) as ResultSortScoreCriteria);
    return (resultScoreCriteria || {
      id: ResultSortCriteriaIdEnum.SCORE,
      value: '0',
      details: {}
    });
  }

  /**
   * Summary. Find Score Criteria Detail
   *
   * Description. Find any criteria detail given its type by performing a deep search inside a dictionary
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   * @param {Dictionary} details
   *
   * @return {ScoreCriteriaDetail | Dictionary}
   */
  public static findScoreCriteriaDetail(criteriaDetailType: (ScoreCriteriaDetailTypeEnum | string),
    details: Dictionary): (ScoreCriteriaDetail | Dictionary) {
    for (const [currentDetailType, currentDetail] of Object.entries(details)) {
      if (currentDetailType === criteriaDetailType) {
        return currentDetail;
      } else if (!!currentDetail.details) {
        const innerCurrentDetail = this.findScoreCriteriaDetail(criteriaDetailType, currentDetail.details);
        if (!!innerCurrentDetail) {
          return innerCurrentDetail;
        }
      }
    }
  }

  /**
   * Summary. Result ScoreCriteriaDetail icon
   *
   * Description. This will return the associated icon ligature for each ScoreCriteriaDetail
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   *
   * @return {string} icon ligature / undefined if criteriaDetailType is not known.
   */
  public static getResultScoreCriteriaDetailIcon(criteriaDetailType: (RootScoreCriteriaDetailTypeEnum | string)): string {
    return {
      [RootScoreCriteriaDetailTypeEnum.CATALOG_DEFINITION]: 'book',
      [RootScoreCriteriaDetailTypeEnum.BUSINESS_RULES]: 'sliders',
      [RootScoreCriteriaDetailTypeEnum.QUERY_CONTEXT]: 'balloon',
      [RootScoreCriteriaDetailTypeEnum.USER_CONTEXT]: 'user',
      pzn: 'heart'
    }[criteriaDetailType];
  }

  /**
   * Summary. Result ScoreCriteriaDetail messages
   *
   * Description. This will return the necessary messages of each ScoreCriteriaDetail needed in the interface.
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   * @param {ExplainScoreMessages} messages
   *
   * @return {ExplainScoreCriteriaMessages}
   */
  public static getResultScoreCriteriaDetailMessages(criteriaDetailType: (ScoreCriteriaDetailTypeEnum | string),
    messages: ExplainScoreMessages): ExplainScoreCriteriaMessages {
    const criteriaDetailMessages = messages.criterias[criteriaDetailType];
    return (criteriaDetailMessages || {
      label: criteriaDetailType,
      abbr: criteriaDetailType.substring(0, 2)
    });
  }

  /**
   * Summary. Result GroupedScoreCriteria
   *
   * Description. this will return the scoreCriteria with their details grouped
   *
   * @param {ResultSortScoreCriteria} scoreCriteria
   *
   * @return {ResultSortScoreCriteria} grouped scoreCriteria
   */
  private static getResultGroupedScoreCriteria(scoreCriteria: ResultSortScoreCriteria): ResultSortScoreCriteria {
    let groupedDetails = Object.values(RootScoreCriteriaDetailTypeEnum)
      .reduce((details: Dictionary, rootCriteriaDetailType: RootScoreCriteriaDetailTypeEnum) => {
        details[rootCriteriaDetailType] = {
          value: 0,
          details: {}
        };
        return details;
      }, {});
    groupedDetails = Object.entries(scoreCriteria.details).reduce((details: Dictionary, [criteriaDetailType, detail]) => {
      const addCriteriaDetailToGroup = {
        [SearchScoreCriteriaDetailTypeEnum.ORGANIC]: this.addCriteriaDetailToCatalogDefinitionGroup,
        [SearchScoreCriteriaDetailTypeEnum.TERM_POSITION]: this.addCriteriaDetailToCatalogDefinitionGroup,
        [SearchScoreCriteriaDetailTypeEnum.CONFIGURATION]: this.addCriteriaDetailToBusinessRulesGroup,
        [SearchScoreCriteriaDetailTypeEnum.CONTEXTUALIZE]: this.addCriteriaDetailToContextualizeGroup,
        default: this.addDefaultCriteriaDetailToGroup
      };
      return (addCriteriaDetailToGroup[criteriaDetailType] || addCriteriaDetailToGroup.default)(details, criteriaDetailType, detail);
    }, groupedDetails);
    return {
      ...scoreCriteria,
      details: groupedDetails
    };
  }

  private static addCriteriaDetailToCatalogDefinitionGroup(details: Dictionary, criteriaDetailType: SearchScoreCriteriaDetailTypeEnum,
    criteriaDetail): Dictionary {
    const rootCriteriaDetail = details[RootScoreCriteriaDetailTypeEnum.CATALOG_DEFINITION];
    rootCriteriaDetail.value += (criteriaDetail as ScoreCriteriaDetail).value;
    rootCriteriaDetail.details[criteriaDetailType] = criteriaDetail;
    return details;
  }

  private static addCriteriaDetailToBusinessRulesGroup(details: Dictionary, criteriaDetailType: SearchScoreCriteriaDetailTypeEnum,
    criteriaDetail): Dictionary {
    Object.entries(criteriaDetail).forEach(([innerDetailType, innerDetail]: [string, ScoreCriteriaDetail]) => {
      const criteriaDetailInnerType = {
        base: ScoreCriteriaDetailTypeEnum.BASE_CONFIGURATION,
        query: ScoreCriteriaDetailTypeEnum.QUERY_CONFIGURATION
      }[innerDetailType];
      const rootCriteriaDetail = details[RootScoreCriteriaDetailTypeEnum.BUSINESS_RULES];
      rootCriteriaDetail.value += innerDetail.value;
      rootCriteriaDetail.details[criteriaDetailInnerType] = innerDetail;
    });
    return details;
  }

  private static addCriteriaDetailToContextualizeGroup(details: Dictionary, criteriaDetailType: SearchScoreCriteriaDetailTypeEnum,
    criteriaDetail): Dictionary {
    Object.entries(criteriaDetail).forEach(([innerDetailType, innerDetail]: [string, ScoreCriteriaDetail]) => {
      const groupCriteriaDetailType = {
        user: RootScoreCriteriaDetailTypeEnum.USER_CONTEXT,
        query: RootScoreCriteriaDetailTypeEnum.QUERY_CONTEXT
      }[innerDetailType];
      details[groupCriteriaDetailType].value += innerDetail.value;
      details[groupCriteriaDetailType] = innerDetail;
    });
    return details;
  }

  private static addDefaultCriteriaDetailToGroup(details: Dictionary, criteriaDetailType: string, detail): Dictionary {
    details[criteriaDetailType] = detail;
    return details;
  }

}
